import { extractBaseRing, setCurrentLotRing } from "./utils";
import turfContains from '@turf/boolean-contains';
import turfLineSplit from '@turf/line-split';
import turfFlatten from '@turf/flatten';
import turfUnion from '@turf/union';
import turfLength from '@turf/length';
import turfAlong from '@turf/along';
import turfDifference from '@turf/difference';
import turfPolygonToLine from "@turf/polygon-to-line";
import { calculateAnalysedParams, calculateFireFenceParam, calculateInitialAsessmentParams } from "./calculateUtils";

export const lineCoordsToPolygonFeature = (coords, props = {}) => {
  if (!Array.isArray(coords) || coords.length < 3) return null;

  const ring = coords.slice();
  const [fx, fy] = ring[0];
  const [lx, ly] = ring[ring.length - 1];
  const isClosed = fx === lx && fy === ly;
  if (!isClosed) ring.push([fx, fy]);

  return {
    type: 'Feature',
    properties: { ...props },
    geometry: { type: 'Polygon', coordinates: [ring] }
  };
};

export const updateLineFromExistingPolygon = (map, polygon, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams) => {
  if (!polygon || !polygon.geometry) return;

  const ringForCache = extractBaseRing(polygon.geometry);
  setCurrentLotRing(map, ringForCache);

  const landcoverFeatures = map.queryRenderedFeatures({ layers: ['landcover-2'] });

  const lineColor = [
    'match', ['get', 'class'],
    'wood', 'rgb(0, 19, 124)',
    'scrub', 'rgb(80, 106, 252)',
    'crop', 'rgb(143, 160, 255)',
    'grass', 'rgb(207, 214, 255)',
    'rgb(255, 147, 147)',
  ];

  const polygonColor = [
    'match', ['get', 'class'],
    'wood', 'rgb(94, 0, 0)',
    'scrub', 'rgb(176, 3, 3)',
    'crop', 'rgb(231, 101, 6)',
    'grass', 'rgb(218, 197, 7)',
    'rgb(85, 193, 0)',
  ];

  const isPoly = polygon.geometry?.type === 'Polygon' || polygon.geometry?.type === 'MultiPolygon';
  const baseLine = isPoly ? turfPolygonToLine({ type: 'Feature', geometry: polygon.geometry }) : { type: 'Feature', geometry: polygon.geometry };
  const lineColorFromMode = isPoly ? polygonColor : lineColor;

  const grouped = landcoverFeatures.reduce((acc, f) => {
    const c = f?.properties?.class || 'other';
    if (!acc[c]) acc[c] = [];
    acc[c].push(f);
    return acc;
  }, {});

  const united = Object.values(grouped).map((arr) => {
    return arr.reduce((acc, f) => acc ? { ...turfUnion(acc, f), properties: f.properties } : f, null);
  });

  const cropped = united.map((f) => {
    const others = united.filter(o => o && o.properties?.class !== f.properties?.class);
    return others.reduce((acc, o) => turfDifference(acc, o) ?? acc, f);
  });

  const splittedLine = cropped.reduce((lines, landcover) => {
    return lines.flatMap(line => {
      if (line.properties?.class) return line;
      const splitted = turfLineSplit(line, landcover).features;
      const parts = (splitted.length > 0 ? splitted : [line]).map(seg => {
        const polygons = landcover.geometry.type === 'MultiPolygon' ? turfFlatten(landcover).features : [landcover];
        const len = turfLength(seg);
        const mid = turfAlong(seg, len / 2);
        if (polygons.some(p => turfContains(p, mid)) && seg.properties?.class !== 'snow') {
          seg.properties = { ...(seg.properties || {}), class: landcover.properties.class };
        }
        return seg;
      });
      return parts;
    });
  }, [baseLine]);

  const fc = { type: 'FeatureCollection', features: splittedLine };

  if (map.getSource('line')) {
    map.getSource('line').setData(fc);
  } else {
    map.addSource('line', { type: 'geojson', data: fc });
  }

  if (!map.getLayer('line')) {
    map.addLayer({
      id: 'line',
      source: 'line',
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#FFF', 'line-width': 10 },
    });
  }

  if (!map.getLayer('line-outline')) {
    map.addLayer({
      id: 'line-outline',
      source: 'line',
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': lineColorFromMode, 'line-width': 5 },
    });
  } else {
    map.setPaintProperty('line-outline', 'line-color', lineColorFromMode);
  }

  calculateFireFenceParam(map, polygon, setFireFenceParams);
  calculateInitialAsessmentParams(map, polygon, setInitialAsessmentParams);
  calculateAnalysedParams(map, polygon, setAnalysedParams);
};